import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import {
  clearDashboardQueueList,
  createNewDashboard,
  resetDashboard,
  setDashboardQueueList,
  setDashboardUserList,
  setFilterByStatus,
  setRefreshDelay,
  toggleDashboardQueue,
  toggleDashboardUser,
  toggleGroupByStatus,
  toggleShowAllUsers,
  toggleShowQueues,
  toggleShowSetup,
} from "@/store/reducers/dashboard";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, {
  viAngleDown,
  viArrowRotateRight,
  viClock,
  viGauge,
  viLayers,
  viList,
  viListChecks,
  viListChecksXmark,
  viPeople,
  viPerson,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import SettingsOption from "./components/SettingsOption";

export default function Dashboard() {
  const dispatch = useDispatch();
  const colors = useAccessibleColors();

  const [tab, setTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  const pbx = useSelector((state) => state.domain.pbx);
  const dashboardSettings = useSelector((state) => state.dashboard.find((_pbx) => _pbx.context === pbx.context));

  if (dashboardSettings === undefined) {
    dispatch(createNewDashboard({ pbx: pbx.context }));
  }

  const auth = useSelector((state) => state.auth);
  const userStore = useSelector((state) => state.domain.users);
  const queueStore = useSelector((state) => state.domain.queues);

  useEffect(() => {
    if (auth.accessLevel <= 3) {
      Promise.all([Api.getUsers(), Api.getQueues()]);
    } else {
      Promise.resolve(Api.getUsers());
    }
  }, [auth.accessLevel]);

  // Options menu
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const openStatusMenu = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };
  const closeStatusMenu = (status) => {
    if (status) {
      dispatch(setFilterByStatus({ pbx: pbx.context, status }));
    }

    setStatusAnchorEl(null);
  };

  // Refresh menu
  const [refreshAnchorEl, setRefreshAnchorEl] = useState(null);
  const refreshMenuOpen = Boolean(refreshAnchorEl);
  const openRefreshMenu = (event) => {
    setRefreshAnchorEl(event.currentTarget);
  };
  const closeRefreshMenu = (refreshDelay) => {
    if (refreshDelay) {
      dispatch(setRefreshDelay({ pbx: pbx.context, refreshDelay }));
    }

    setRefreshAnchorEl(null);
  };

  const dashboardSettingsUserList = useSearch(userStore.list, searchValue, ["name", "exten"]);

  const dashboardSettingsQueueList = useSearch(queueStore.list, searchValue, ["name", "exten"]);

  return (
    <Stack spacing={2}>
      <HeaderCard
        avatar={<Avatar text={pbx.name} />}
        title="Dashboard"
        subheader={pbx.name}
        tab={tab}
        changeTab={(e, _tab) => setTab(_tab)}
        tabs={[
          { label: "General", icon: viGauge },
          { label: "Users", icon: viPerson },
          { label: "Queues", icon: viList },
        ]}
      />
      {dashboardSettings && (
        <>
          {tab === 0 && (
            <>
              <FormGroup title="General">
                <Grid size={12}>
                  <List>
                    <SettingsOption onClick={() => dispatch(toggleShowSetup({ pbx: pbx.context }))}>
                      <ListItemIcon>
                        <Valkyrie icon={viGauge} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Show setup on Dashboard"
                        secondary="Show the setup wizard on the Dashboard."
                      />
                      <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                        <Switch checked={dashboardSettings.settings.showSetup} sx={{ m: 0, mt: -0.5 }} />
                      </Box>
                    </SettingsOption>
                    <SettingsOption onClick={openRefreshMenu}>
                      <ListItemIcon>
                        <Valkyrie icon={viClock} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Refresh interval"
                        secondary="Amount of time before the available users and queues are refreshed."
                      />
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          gap: 0.5,
                          pl: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ m: 0 }}>
                          {dashboardSettings.settings.refreshDelay} minute
                          {dashboardSettings.settings.refreshDelay >= 2 ? "s" : ""}
                        </Typography>
                        <Valkyrie icon={viAngleDown} />
                      </Stack>
                    </SettingsOption>
                  </List>
                  <Menu
                    anchorEl={refreshAnchorEl}
                    open={refreshMenuOpen}
                    onClose={() => closeRefreshMenu()}
                    slotProps={{
                      paper: {
                        elevation: 1,
                        sx: {
                          mt: 0.5,
                          border: "1px solid",
                          borderColor: "divider",
                          minWidth: 140,
                        },
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {[1, 2, 3, 5].map((minutes) => (
                      <MenuItem
                        key={minutes}
                        onClick={() => closeRefreshMenu(minutes)}
                        selected={dashboardSettings.settings.refreshDelay === minutes}
                        sx={{ px: 1.5 }}
                      >
                        {minutes} minute{minutes >= 2 ? "s" : ""}
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>
              </FormGroup>
              <FormGroup title="Users">
                <Grid size={12}>
                  <List>
                    <SettingsOption onClick={openStatusMenu}>
                      <ListItemIcon>
                        <Valkyrie icon={viPerson} />
                      </ListItemIcon>
                      <ListItemText primary="Filter by status" />
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          gap: 0.5,
                          pl: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ m: 0 }}>
                          {dashboardSettings.settings.filterByStatus === "active"
                            ? "Available & busy"
                            : dashboardSettings.settings.filterByStatus === "inactive"
                              ? "Unavailable"
                              : "All"}
                        </Typography>
                        <Valkyrie icon={viAngleDown} />
                      </Stack>
                    </SettingsOption>
                    <Menu
                      anchorEl={statusAnchorEl}
                      open={statusMenuOpen}
                      onClose={() => closeStatusMenu()}
                      slotProps={{
                        paper: {
                          elevation: 1,
                          sx: {
                            mt: 0.5,
                            border: "1px solid",
                            borderColor: "divider",
                            minWidth: 140,
                          },
                        },
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={() => closeStatusMenu("all")}
                        selected={dashboardSettings.settings.filterByStatus === "all"}
                        sx={{ px: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "center",
                            gap: 0.5,
                            width: 32,
                            mr: 1.5,
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.green }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.amber }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.grey }} />
                        </Stack>
                        All
                      </MenuItem>
                      <MenuItem
                        onClick={() => closeStatusMenu("active")}
                        selected={dashboardSettings.settings.filterByStatus === "active"}
                        sx={{ px: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "center",
                            gap: 0.5,
                            width: 32,
                            mr: 1.5,
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.green }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.amber }} />
                        </Stack>
                        Available & busy
                      </MenuItem>
                      <MenuItem
                        onClick={() => closeStatusMenu("inactive")}
                        selected={dashboardSettings.settings.filterByStatus === "inactive"}
                        sx={{ px: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "center",
                            gap: 0.5,
                            width: 32,
                            mr: 1.5,
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.grey }} />
                        </Stack>
                        Unavailable
                      </MenuItem>
                    </Menu>
                    <SettingsOption onClick={() => dispatch(toggleGroupByStatus({ pbx: pbx.context }))}>
                      <ListItemIcon>
                        <Valkyrie icon={viLayers} />
                      </ListItemIcon>
                      <ListItemText primary="Group by status" />
                      <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                        <Switch checked={dashboardSettings.settings.groupByStatus} sx={{ m: 0, mt: -0.5 }} />
                      </Box>
                    </SettingsOption>
                    <SettingsOption onClick={() => dispatch(toggleShowAllUsers({ pbx: pbx.context }))}>
                      <ListItemIcon>
                        <Valkyrie icon={viPeople} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Show all users"
                        secondary="Disable this to customize which users you want to see."
                      />
                      <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                        <Switch checked={dashboardSettings.settings.showAllUsers} sx={{ m: 0, mt: -0.5 }} />
                      </Box>
                    </SettingsOption>
                  </List>
                </Grid>
              </FormGroup>
              {auth.accessLevel <= 3 && (
                <FormGroup title="Queues">
                  <Grid size={12}>
                    <List>
                      <SettingsOption onClick={() => dispatch(toggleShowQueues({ pbx: pbx.context }))}>
                        <ListItemIcon>
                          <Valkyrie icon={viList} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Show queues"
                          secondary="Enable this to select which queues you want on your Dashboard."
                        />
                        <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                          <Switch checked={dashboardSettings.settings.showQueues} sx={{ m: 0, mt: -0.5 }} />
                        </Box>
                      </SettingsOption>
                    </List>
                  </Grid>
                </FormGroup>
              )}
              <FormGroup title="Reset">
                <Grid size={12}>
                  <List>
                    <SettingsOption onClick={() => dispatch(resetDashboard({ pbx: pbx.context }))}>
                      <ListItemIcon>
                        <Valkyrie icon={viArrowRotateRight} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Reset Dashboard"
                        secondary="Reset the Dashboard to its default settings."
                      />
                    </SettingsOption>
                  </List>
                </Grid>
              </FormGroup>
            </>
          )}
          {tab === 1 && (
            <FormGroup title="Visible users">
              <Grid size={12}>
                <List>
                  <SettingsOption onClick={() => dispatch(toggleShowAllUsers({ pbx: pbx.context }))}>
                    <ListItemIcon>
                      <Valkyrie icon={viPeople} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Show all users"
                      secondary="Disable this to customize which users you want to see."
                    />
                    <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                      <Switch checked={dashboardSettings.settings.showAllUsers} sx={{ m: 0, mt: -0.5 }} />
                    </Box>
                  </SettingsOption>
                </List>
              </Grid>
              <Grid size={12}>
                <Stack
                  direction="row"
                  sx={{
                    gap: 0.5,
                    mb: 2,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  <Search
                    blur
                    accessKey="q"
                    value={searchValue}
                    onChange={setSearchValue}
                    sx={{ backgroundColor: "transparent" }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        gap: 0.5,
                        mr: 0.25,
                      }}
                    >
                      <Tooltip title="Select all" placement="top">
                        <span>
                          <IconButton
                            disabled={
                              dashboardSettings.settings.showAllUsers ||
                              dashboardSettings.userList.length === userStore.list.length
                            }
                            onClick={() =>
                              dispatch(
                                setDashboardUserList({
                                  pbx: pbx.context,
                                  list: userStore.list.map((_user) => _user.id),
                                }),
                              )
                            }
                          >
                            <Valkyrie icon={viListChecks} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Clear selection" placement="top">
                        <span>
                          <IconButton
                            disabled={
                              dashboardSettings.settings.showAllUsers || dashboardSettings.userList.length === 0
                            }
                            onClick={() => dispatch(setDashboardUserList({ pbx: pbx.context, list: [] }))}
                          >
                            <Valkyrie icon={viListChecksXmark} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Search>
                </Stack>
                <List
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(19rem, 100%), 1fr))",
                  }}
                >
                  {dashboardSettingsUserList.map((user) => (
                    <ListItem
                      key={user.id}
                      disablePadding
                      sx={{ minHeight: 40 }}
                      secondaryAction={
                        <Checkbox
                          size="small"
                          tabIndex={-1}
                          edge="end"
                          disableRipple
                          disabled={dashboardSettings.settings.showAllUsers}
                          checked={dashboardSettings.userList.includes(user.id)}
                          onClick={() => dispatch(toggleDashboardUser({ pbx: pbx.context, userId: user.id }))}
                        />
                      }
                    >
                      <ListItemButton
                        sx={{ minHeight: 40, py: 0.5 }}
                        disabled={dashboardSettings.settings.showAllUsers}
                        onClick={() => dispatch(toggleDashboardUser({ pbx: pbx.context, userId: user.id }))}
                      >
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                          <Avatar text={user.name} px={24} />
                        </ListItemAvatar>
                        <ListItemText primary={user.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {dashboardSettingsUserList.length === 0 && (
                  <List>
                    <ListItem>
                      <ListItemText primary="No users available" />
                    </ListItem>
                  </List>
                )}
              </Grid>
            </FormGroup>
          )}
          {auth.accessLevel <= 3 && tab === 2 && (
            <FormGroup title="Visible queues">
              <Grid size={12}>
                <List>
                  <SettingsOption onClick={() => dispatch(toggleShowQueues({ pbx: pbx.context }))}>
                    <ListItemIcon>
                      <Valkyrie icon={viList} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Show queues"
                      secondary="Enable this to select which queues you want on your Dashboard."
                    />
                    <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                      <Switch checked={dashboardSettings.settings.showQueues} sx={{ m: 0, mt: -0.5 }} />
                    </Box>
                  </SettingsOption>
                </List>
              </Grid>
              <Grid size={12}>
                <Stack
                  direction="row"
                  sx={{
                    gap: 0.5,
                    mb: 2,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  <Search
                    blur
                    accessKey="q"
                    value={searchValue}
                    onChange={setSearchValue}
                    sx={{ backgroundColor: "transparent" }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        gap: 0.5,
                        mr: 0.25,
                      }}
                    >
                      <Tooltip title="Select all" placement="top">
                        <span>
                          <IconButton
                            disabled={
                              !dashboardSettings.settings.showQueues ||
                              dashboardSettings.queueList.length >= 20 ||
                              dashboardSettings.queueList.length === queueStore.list.length
                            }
                            onClick={() =>
                              dispatch(
                                setDashboardQueueList({
                                  pbx: pbx.context,
                                  list: queueStore.list.map((_queue) => _queue.id),
                                }),
                              )
                            }
                          >
                            <Valkyrie icon={viListChecks} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Clear selection" placement="top">
                        <span>
                          <IconButton
                            disabled={
                              !dashboardSettings.settings.showQueues || dashboardSettings.queueList.length === 0
                            }
                            onClick={() => dispatch(clearDashboardQueueList({ pbx: pbx.context }))}
                          >
                            <Valkyrie icon={viListChecksXmark} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Search>
                </Stack>
                <List
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(19rem, 100%), 1fr))",
                  }}
                >
                  {dashboardSettingsQueueList.map((queue) => (
                    <ListItem
                      key={queue.id}
                      disablePadding
                      sx={{ minHeight: 40 }}
                      secondaryAction={
                        <Checkbox
                          size="small"
                          tabIndex={-1}
                          edge="end"
                          disableRipple
                          disabled={!dashboardSettings.settings.showQueues}
                          checked={!!dashboardSettings.queueList.find((_queue) => _queue.id === queue.id)}
                          onClick={() => dispatch(toggleDashboardQueue({ pbx: pbx.context, queueId: queue.id }))}
                        />
                      }
                    >
                      <ListItemButton
                        sx={{ minHeight: 40, py: 0.5 }}
                        disabled={!dashboardSettings.settings.showQueues}
                        onClick={() => dispatch(toggleDashboardQueue({ pbx: pbx.context, queueId: queue.id }))}
                      >
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                          <Avatar text={queue.name} icon={viList} px={24} />
                        </ListItemAvatar>
                        <ListItemText primary={queue.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {dashboardSettingsQueueList.length === 0 && (
                  <List>
                    <ListItem>
                      <ListItemText primary="No queues available" />
                    </ListItem>
                  </List>
                )}
              </Grid>
            </FormGroup>
          )}
        </>
      )}
    </Stack>
  );
}
